import { memo, type MouseEvent, useCallback, useMemo } from 'react';
import { Stack, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { Areas, HighwayArea } from '@thive/fddm-types';
import FlipIcon from '@mui/icons-material/Flip';

import {
  LaneLeftHandIcon,
  LaneLeftRightIcon,
  LaneRightHandIcon,
  LaneRightIcon,
} from '@/assets/icons';
import { minItemsArrayUtils } from '@/modules/floorplanDesign/helpers/minItems';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { useAppStore } from '@/modules/app/store/useAppStore';

type HighwayRoadType = HighwayArea['highwayPoiSpecs'][number]['roadType'];

type LaneDirectionProps = {
  selectedHighway: HighwayArea;
};

const controlsSx = {
  pointerEvents: 'auto',
};

function LaneDirectionComponent({ selectedHighway }: LaneDirectionProps) {
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);
  const mode = useAppStore((state) => state.mode);

  const selectedHighwaySegmentIds = useMemo(
    () => new Set(selectedHighway.segments.map((segment) => segment.id)),
    [selectedHighway.segments],
  );

  const hasJitRouting = useFloorplanDesignStore(
    useCallback(
      (state) =>
        state.design.justInTimeRoutingConfigs.some((jitRoutingConfig) =>
          selectedHighwaySegmentIds.has(jitRoutingConfig.highwayAreaId),
        ),
      [selectedHighwaySegmentIds],
    ),
  );

  const isPicking = useAppStore((state) => state.selectedToolId === 'choose');

  const { roadType } = selectedHighway.highwayPoiSpecs[0];
  const isDisabled = mode !== 'EDITABLE';
  const roadTypeDisabled = isDisabled || hasJitRouting || isPicking;
  const flipDisabled = roadType === 'TWO_WAY_INDIA_STYLE';

  const setRoadType = useCallback(
    (area: HighwayArea, newValue: HighwayRoadType) => {
      fddmSvc.setPreference('highwayArea.highwayPoiSpecs.roadType', newValue);

      const isOneWay = newValue === 'ONE_WAY';
      const connectUturns: HighwayArea['connectUturns'] = isOneWay
        ? 'NO_UTURN_MAPPING'
        : fddmSvc.getPreference('highwayArea.connectUturns');

      // Only persist the preference when it's a meaningful 2-way value
      if (!isOneWay) {
        fddmSvc.setPreference('highwayArea.connectUturns', connectUturns);
      }

      const updatedHighwayPoiSpecs: HighwayArea['highwayPoiSpecs'] = minItemsArrayUtils.mapAtLeast1(area.highwayPoiSpecs, (spec) => ({
        ...spec,
        roadType: newValue,
      }));

      fddmSvc.updateArea({
        ...area,
        connectUturns,
        highwayPoiSpecs: updatedHighwayPoiSpecs,
      });
    },
    [fddmSvc],
  );

  const flipHighwayDirection = useCallback(
    (highway: HighwayArea) => {
      const { segments } = highway;
      const updatedAreas: Areas = {
        highwayAreas: [
          {
            ...highway,
            segments: minItemsArrayUtils.mapAtLeast1(segments, (segment, index) => ({
              ...segment,
              start: segments[segments.length - 1 - index].end,
              end: segments[segments.length - 1 - index].start,
              height: segments[segments.length - 1 - index].height,
            })),
          },
        ],
        rackingAreas: [],
        singularStationAreas: [],
        stationaryAreas: [],
        manualAreas: [],
      };

      fddmSvc.updateAreas(updatedAreas);
    },
    [fddmSvc],
  );

  const onRoadTypeChange = useCallback(
    (_event: MouseEvent<HTMLElement>, input: HighwayRoadType | null) => {
      if (roadTypeDisabled) return;
      if (!input || input === roadType) return;

      setRoadType(selectedHighway, input);
    },
    [roadType, roadTypeDisabled, selectedHighway, setRoadType],
  );

  const onFlipHighway = useCallback(() => {
    if (isDisabled || flipDisabled) return;

    if (roadType === 'TWO_WAY_RIGHT_HAND_TRAFFIC') {
      setRoadType(selectedHighway, 'TWO_WAY_LEFT_HAND_TRAFFIC');
      return;
    }

    if (roadType === 'TWO_WAY_LEFT_HAND_TRAFFIC') {
      setRoadType(selectedHighway, 'TWO_WAY_RIGHT_HAND_TRAFFIC');
      return;
    }

    flipHighwayDirection(selectedHighway);
  }, [flipDisabled, flipHighwayDirection, isDisabled, roadType, selectedHighway, setRoadType]);

  return (
    <Stack direction='row' spacing={1} sx={controlsSx}>
      <ToggleButtonGroup value={roadType} exclusive onChange={onRoadTypeChange}>
        <ToggleButton
          value='TWO_WAY_INDIA_STYLE'
          aria-label='Two way india style'
          disabled={roadTypeDisabled}
        >
          <LaneLeftRightIcon />
        </ToggleButton>

        {roadType === 'TWO_WAY_LEFT_HAND_TRAFFIC' ? (
          <ToggleButton
            value='TWO_WAY_LEFT_HAND_TRAFFIC'
            aria-label='Two way left hand traffic'
            disabled={roadTypeDisabled}
          >
            <LaneLeftHandIcon />
          </ToggleButton>
        ) : (
          <ToggleButton
            value='TWO_WAY_RIGHT_HAND_TRAFFIC'
            aria-label='Two way right hand traffic'
            disabled={roadTypeDisabled}
          >
            <LaneRightHandIcon />
          </ToggleButton>
        )}

        <ToggleButton value='ONE_WAY' aria-label='One way'>
          <LaneRightIcon />
        </ToggleButton>
      </ToggleButtonGroup>

      <ToggleButton
        value='FLIP'
        disabled={isDisabled || flipDisabled}
        onClick={onFlipHighway}
        aria-label='Flip'
      >
        <FlipIcon />
      </ToggleButton>
    </Stack>
  );
}

export const LaneDirection = memo(LaneDirectionComponent);
